import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, exhaustMap, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { displayNameOrEmail } from '@ninety/platform/models/_shared/display-name-or-email';
import { RoleCode } from '@ninety/platform/models/_shared/role-code';
import { User } from '@ninety/platform/models/_shared/user';
import { StripeReason } from '@ninety/platform/models/billing/stripe-query-params';
import { isGSGTargetCompany } from '@ninety/platform/models/company/is-gsg-target-company';
import { SnackbarTemplateType } from '@ninety/platform/models/enums/snackbar-template-type';
import { AddTeammateFormData } from '@ninety/platform/models/user/add-teammate-form';
import { NewUserDto } from '@ninety/platform/models/user/new-user.dto';
import { BillingV3Service } from '@ninety/platform/services/services/billing-v3.service';
import { FilterService } from '@ninety/platform/services/services/filter.service';
import { RoleService } from '@ninety/platform/services/services/role.service';
import { LocalStorageService, SessionStorageService } from '@ninety/platform/services/services/storage.service';
import { TimeService } from '@ninety/platform/services/services/time.service';

import { BillingStateActions } from '../../app-global/billing/billing-state.actions';
import {
  selectCompanyBillingCounts,
  selectIsFreeOrTrialing,
  selectStripeQueryParams,
} from '../../app-global/billing/billing-state.selectors';
import { selectCompany, selectIsBillingV3Company } from '../../app-global/company/company-state.selectors';
import { NotificationActions } from '../../app-global/notifications/notification.actions';
import { SpinnerActions } from '../../app-global/spinner/spinner-state.actions';
import { appActions } from '../../app.actions';
import { CONFIRM_DIALOG } from '../../tokens/billing-dialog.tokens';
import { USER_SERVICE } from '../../tokens/user-service.token';
import { ADD_TEAMMATES_DIALOG } from '../../tokens/users-dialog.tokens';
import { FeatureFlagKeys } from '../feature-flag/feature-flag-state.model';
import { selectFeatureFlag } from '../feature-flag/feature-flag-state.selectors';
import { UserTeamsActions } from '../team-list/team-list-state.actions';
import { UserListModel } from '../user-list/api/user-list.model';
import { UserListStateActions } from '../user-list/user-list-state.actions';

import {
  AddTeammatesActions,
  UserPreferencesActions,
  UserSettingsActions,
  UsersStateActions,
} from './users-state.actions';
import { selectCurrentUser, selectCurrentUserId, UserSelectors } from './users-state.selectors';

function toUserListModel(u: User): UserListModel {
  const first = u.metadata?.name?.first ?? '';
  const last = u.metadata?.name?.last ?? '';
  const derivedName = first || last ? `${first} ${last}`.trim() : '';
  return {
    _id: u._id ?? '',
    active: u.active ?? false,
    avatarUrl: u.metadata?.picture?.url ?? '',
    firstName: first,
    fullName: u.fullName || derivedName || u.primaryEmail || '',
    initials: `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase(),
    isImplementer: u.isImplementer ?? false,
    lastName: last,
    name: derivedName,
    roleCode: u.roleCode ?? RoleCode.managee,
    teamIds: u.teams?.map(t => t.teamId) ?? [],
    title: u.title ?? '',
  };
}

const ADD_TEAMMATES_FORM_DATA = 'addTeammatesFormData';

@Injectable()
export class UsersStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly router = inject(Router);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly store = inject(Store);
  private readonly timeService = inject(TimeService);
  private readonly billingV3Service = inject(BillingV3Service);
  private readonly http = inject(HttpClient);

  filterService = inject(FilterService);
  // Token-inverted (DEV-26105 T6c): `UserService` + the dialog components live in `shared/ui/core`,
  // so they're injected through platform-resident tokens bound at the app composition root rather than
  // imported directly — keeps this effect free of the `platform/state -> shared/ui/core` edge.
  private readonly userService = inject(USER_SERVICE);
  private readonly openAddTeammates = inject(ADD_TEAMMATES_DIALOG);
  private readonly openConfirm = inject(CONFIRM_DIALOG);
  timezonePromptFlag$ = this.store.select(selectFeatureFlag(FeatureFlagKeys.timezonePrompt));

  fetchTier1Data$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.fetchTier1Data),
      map(() => UsersStateActions.fetchUsers())
    )
  );

  fetchUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.fetchUsers),
      switchMap(() =>
        this.http.get<User[]>('/api/v4/Users').pipe(map(users => UsersStateActions.loadAllUsers({ users })))
      )
    )
  );

  /**
   * Populates the user-list store by deriving slim UserListModel[] from the full User[].
   * This replaces the separate POST /api/v4/Users/List call — the full /Users response
   * contains all 12 UserListModel fields, just nested under metadata instead of flat.
   */
  deriveUserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.loadAllUsers),
      map(({ users }) => UserListStateActions.getUserListSuccess({ users: users.map(toUserListModel) }))
    )
  );

  syncUserListOnAdd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.addOne),
      map(({ user }) => UserListStateActions.addItems({ users: [toUserListModel(user)] }))
    )
  );

  syncUserListOnAddMany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.addMany),
      map(({ users }) => UserListStateActions.addItems({ users: users.map(toUserListModel) }))
    )
  );

  syncUserListOnRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.removeOne, UsersStateActions.deleteUserSuccess),
      map(({ _id }) => UserListStateActions.removeItem({ _id }))
    )
  );

  setTier1Data_checkForTimezone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.setCurrentUser),
      map(() => UsersStateActions.checkToShowTimeZoneUpdateDialog())
    )
  );

  setCurrentUser_UpdateFilterService$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UsersStateActions.setCurrentUser),
        tap(({ user }) => this.filterService.userUpdated(user))
      ),
    {
      dispatch: false,
    }
  );

  updateUserKpiOrder = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.updateUserKpiOrder),
      switchMap(({ userId, userKpiOrdinals, periodInterval }) =>
        this.userService.updateUserKpiOrder({ userId, userKpiOrdinals, periodInterval }).pipe(
          map(() => UsersStateActions.updateUserKpiOrderSuccess()),
          catchError((error: unknown) => of(UsersStateActions.updateUserKpiOrderFailed({ error })))
        )
      )
    )
  );

  notifyOnUpdateUserKpiOrderFailed = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.updateUserKpiOrderFailed),
      map(({ error }) =>
        NotificationActions.notifyError({
          error: error,
          message: 'Could not update item order.',
        })
      )
    )
  );

  dismissManagerPermissionChangesNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.dismissManagerPermissionChangesNotification),
      concatLatestFrom(() => this.store.select(selectCurrentUserId)),
      map(([_, userId]) =>
        UsersStateActions.update({ userId, update: { managerPermissionChangesNotificationDismissed: true } })
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.update),
      concatMap(({ userId, update }) =>
        // api hands back entire updated user
        this.userService.update(update, userId).pipe(map(() => ({ userId, update })))
      ),
      map(({ userId: _id, update: changes }) => UsersStateActions.updateSuccess({ _id, changes }))
    )
  );

  updateUserQuietly$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.updateQuietly),
      switchMap(({ userId, update }) =>
        this.userService.updateQuietly(userId, update).pipe(
          map(() => UsersStateActions.updateQuietlySuccess({ _id: userId, changes: update })),
          catchError((error: unknown) => of(UsersStateActions.updateQuietlyFailed({ error })))
        )
      )
    )
  );

  /** same actions as update, but can have billing implications so separating from typical update action */
  updateUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.updateRole),
      concatMap(({ userId, update }) =>
        // api hands back entire updated user
        this.userService.update(update, userId).pipe(
          map(() => UsersStateActions.updateRoleSuccess({ _id: userId, changes: update })),
          catchError((error: unknown) => of(UsersStateActions.updateRoleFailed({ _id: userId, error })))
        )
      )
    )
  );

  /** since we have other listeners besides the user state reducer for update success,
   *  just pass it along instead of adding it to all the places */
  updateUserRoleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.updateRoleSuccess),
      map(({ _id, changes }) => UsersStateActions.updateSuccess({ _id, changes }))
    )
  );

  updateUserRoleFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.updateRoleFailed),
      map(({ error }) =>
        NotificationActions.notifyError({ error: error, message: 'Could not update user role.  Please try again.' })
      )
    )
  );

  addUsersToTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserTeamsActions.addUsersToTeam),
      concatMap(({ users, teamId }) => {
        const updates = users.map(u => {
          if (u.teams.some(t => t.teamId === teamId)) {
            return;
          }
          return this.userService.update({ teams: [...u.teams, { teamId }] }, u._id);
        });
        return forkJoin(updates);
      }),
      map(users => UserTeamsActions.addUsersToTeamSuccess({ users })),
      catchError((error: unknown) => of(UserTeamsActions.addUsersToTeamFailed({ error })))
    )
  );

  removeUserFromTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserTeamsActions.removeUserFromTeam),
      concatLatestFrom(({ userId }) => this.store.select(UserSelectors.selectUserById(userId))),
      concatMap(([{ userId, teamId }, user]) =>
        this.userService.update({ teams: [...user.teams.filter(t => t.teamId !== teamId)] }, user._id).pipe(
          map(() => UserTeamsActions.removeUserFromTeamSuccess({ userId, teamId })),
          catchError((error: unknown) => of(UserTeamsActions.removeUserFromTeamFailed({ error })))
        )
      )
    )
  );

  userUpdateStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserTeamsActions.removeUserFromTeam,
        UserTeamsActions.addUsersToTeam,
        BillingStateActions.changeLicenseCount,
        AddTeammatesActions.createUsers
      ),
      map(action => SpinnerActions.startPrimary({ source: action.type }))
    )
  );

  userUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserTeamsActions.removeUserFromTeamSuccess,
        UserTeamsActions.removeUserFromTeamFailed,
        UserTeamsActions.addUsersToTeamFailed,
        UserTeamsActions.addUsersToTeamSuccess,
        AddTeammatesActions.createUsersSuccess,
        AddTeammatesActions.createUsersFailed
      ),
      map(action => SpinnerActions.stopPrimary({ source: action.type }))
    )
  );

  updateUserTutorials = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActions.hideUserTutorial),
      concatLatestFrom(() => this.store.select(selectCurrentUser)),
      concatMap(([{ userTutorialType }, currentUser]) =>
        this.userService.update({
          tutorialsHidden: { ...currentUser?.tutorialsHidden, [userTutorialType]: true },
        })
      ),
      map(user => UserPreferencesActions.userTutorialsUpdated({ tutorialsHidden: user.tutorialsHidden }))
    )
  );

  checkTimezonePrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.checkToShowTimeZoneUpdateDialog),
      concatLatestFrom(() => [this.store.select(selectCurrentUser), this.timezonePromptFlag$]),
      filter(([_, _user, flagEnabled]) => {
        const setTimezoneAutomatically = _user.settings?.setTimezoneAutomatically;
        const notLoginUrl = !this.router.url.includes('/login/');

        // todo - remove this ridiculousness to actually save to the person and not local storage lolololol
        const dontAskToUpdateTimezoneAgain = this.localStorageService.get('dontAskToUpdateTimezoneAgain') !== 'true';

        return flagEnabled && !setTimezoneAutomatically && notLoginUrl && dontAskToUpdateTimezoneAgain;
      }),
      switchMap(([_, user]) => {
        const browserTimezoneEntry = this.timeService.getBrowserTimezone();
        const userTimeZoneNotFormatted = user?.settings?.timezone;
        const shouldShowPromptOutsideOfLoginFlow = userTimeZoneNotFormatted !== browserTimezoneEntry.name;

        if (shouldShowPromptOutsideOfLoginFlow) {
          return this.userService
            .showTimezoneUpdateDialog(browserTimezoneEntry)
            .pipe(map((user: User) => UserSettingsActions.updateTimezone({ user })));
        }
        return of(UserSettingsActions.timezoneNotUpdated());
      })
    )
  );

  updateAutomaticTimezone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.checkToShowTimeZoneUpdateDialog),
      concatLatestFrom(() => [this.store.select(selectCurrentUser)]),
      filter(([_, _user]) => {
        const setTimezoneAutomatically = _user.settings?.setTimezoneAutomatically;
        const notLoginUrl = !this.router.url?.includes('/login/');

        return setTimezoneAutomatically && notLoginUrl;
      }),
      switchMap(([_, user]) => {
        const browserTimezoneEntry = this.timeService.getBrowserTimezone() ?? this.timeService.getTimezoneByName('GMT');
        const userTimeZoneNotFormatted = user?.settings?.timezone;
        const shouldUpdate = userTimeZoneNotFormatted !== browserTimezoneEntry.name;

        if (shouldUpdate) {
          return this.userService
            .updateUserSettingsToBrowserTimezone(browserTimezoneEntry)
            .pipe(map((user: User) => UserSettingsActions.updateTimezone({ user })));
        }
        return of(UserSettingsActions.timezoneNotUpdated());
      })
    )
  );

  updateIssuesListView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserSettingsActions.updateIssuesListView),
        concatMap(({ issuesListView }) => this.userService.updateUserSettings({ issuesListView }))
      ),
    { dispatch: false }
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.deleteUser),
      switchMap(({ _id }) => {
        //Make the call to the backend to delete the user and update the store
        return this.userService.deleteUser(_id).pipe(
          map(() => {
            return UsersStateActions.deleteUserSuccess({ _id }); //Dispatch success action, will refetch directory users
          }),
          catchError((error: unknown) => {
            return of(UsersStateActions.deleteUserFailed({ error }));
          })
        );
      })
    )
  );

  deactivateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersStateActions.deactivateUser),
      switchMap(({ _id }) => {
        //Make the call to the backend to deactivate the user and update the store
        return this.userService.deactivateUser(_id).pipe(
          map(() => {
            return UsersStateActions.deactivateUserSuccess({ _id }); //Dispatch success action, will refetch directory users
          }),
          catchError((error: unknown) => {
            return of(UsersStateActions.deactivateUserFailed({ error }));
          })
        );
      })
    )
  );

  /** new add teammates dialog */
  private addTeammatesDialogRef: MatDialogRef<unknown>;

  /** if create users fails, it will also reopen the dialog with the stored forms from session storage */
  openAddTeammatesDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.openAddTeammatesDialog, AddTeammatesActions.createUsersFailed),
      filter(() => this.addTeammatesDialogRef?.getState() !== MatDialogState.OPEN),
      tap(() => {
        const storedData = this.sessionStorage.get(ADD_TEAMMATES_FORM_DATA);
        this.sessionStorage.delete(ADD_TEAMMATES_FORM_DATA);
        this.addTeammatesDialogRef = this.openAddTeammates({
          width: '684px',
          data: { savedFormData: JSON.parse(storedData) ?? [null] },
        });

        this.addTeammatesDialogRef.afterClosed().subscribe(() => {
          this.sessionStorage.delete(ADD_TEAMMATES_FORM_DATA);
        });
      }),
      /** get billing counts when opening the add teammates dialog,
       * mainly in case of opening it from the left side nav as directory will already fetch them */
      map(() => BillingStateActions.getCompanyBillingCounts())
    )
  );

  closeAddTeammatesDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AddTeammatesActions.closeAddTeammatesDialog, AddTeammatesActions.createUsersSuccess),
        tap(() => {
          this.addTeammatesDialogRef?.close();
        })
      ),
    { dispatch: false }
  );

  openUnsavedChangesDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.openUnsavedChangesDialog),
      switchMap(() =>
        this.openConfirm({
          data: {
            title: 'Unsaved changes',
            message:
              'You’ve made unsaved changes but haven’t invited your new teammate yet. Do you want to go back and continue?',
            confirmButtonText: 'Go back',
            cancelButtonText: 'Discard changes',
            hideXButton: true,
          },
          disableClose: true,
        }).afterClosed()
      ),
      filter(keepChanges => !keepChanges),
      map(() => AddTeammatesActions.closeAddTeammatesDialog())
    )
  );

  /** if free or trialing company, create users.  Otherwise, need to check billing */
  addTeammates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.addTeammates),
      concatLatestFrom(() => [this.store.select(selectIsBillingV3Company), this.store.select(selectIsFreeOrTrialing)]),
      map(([{ userForms }, isBillingV3Company, isFreeOrTrialing]) => {
        if (!isBillingV3Company) {
          return AddTeammatesActions.createUsers({ userForms });
        } else if (isFreeOrTrialing) {
          return AddTeammatesActions.addTeammatesToFreeCompany({ userForms });
        } else {
          return AddTeammatesActions.addTeammatesToPaidCompany({ userForms });
        }
      })
    )
  );

  addTeammatesToPayingCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.addTeammatesToPaidCompany),
      switchMap(({ userForms }) => {
        const emails = userForms.filter(u => u.email).map(u => u?.email);
        return this.userService.checkForHelpfuls(emails).pipe(map(helpfulsRecord => ({ userForms, helpfulsRecord })));
      }),
      concatLatestFrom(() => this.store.select(selectCompanyBillingCounts)),
      map(([{ userForms, helpfulsRecord }, counts]) => {
        const numPaidUsersToCreate = userForms.filter(
          ({ role, email }) => RoleService.isPaidRole(role) && !helpfulsRecord[email.toLowerCase()]
        ).length;

        if (numPaidUsersToCreate > 0 && counts.assignableSeats < numPaidUsersToCreate) {
          this.sessionStorage.set(ADD_TEAMMATES_FORM_DATA, JSON.stringify(userForms));
          return BillingStateActions.changeLicenseCount({
            quantity: counts.seatsPaidFor + counts.activeBillingUsers + numPaidUsersToCreate - counts.seatsPaidFor,
          });
        }

        return AddTeammatesActions.createUsers({ userForms });
      })
    )
  );

  addTeammatesToFreeCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.addTeammatesToFreeCompany),
      switchMap(({ userForms }) => {
        const emails = userForms.filter(u => u.email).map(u => u?.email);
        return this.userService.checkForHelpfuls(emails).pipe(map(helpfulsRecord => ({ userForms, helpfulsRecord })));
      }),
      concatLatestFrom(() => this.store.select(selectCompanyBillingCounts)),
      switchMap(([{ userForms, helpfulsRecord }, counts]) => {
        const numPaidUsersToCreate = userForms.filter(
          ({ role, email }) => RoleService.isPaidRole(role) && !helpfulsRecord[email.toLowerCase()]
        ).length;

        if (counts.assignableSeats < numPaidUsersToCreate) {
          this.sessionStorage.set(ADD_TEAMMATES_FORM_DATA, JSON.stringify(userForms));

          return this.billingV3Service
            .updateSubscriptionQuantity(counts.seatsPaidFor + numPaidUsersToCreate - counts.assignableSeats)
            .pipe(map(() => AddTeammatesActions.createUsers({ userForms })));
        }

        return of(AddTeammatesActions.createUsers({ userForms }));
      })
    )
  );

  /** if canceling license change, clear the session storage */
  cancelLicenseChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(BillingStateActions.cancelLicenseChange),
        tap(() => this.sessionStorage.delete(ADD_TEAMMATES_FORM_DATA))
      ),
    { dispatch: false }
  );

  /** after getting updated subscription, if there are saved forms, recall add teammates.
   * This will recalculate and compare the number of new paid users,
   * but we don't know if the user successfully added new licenses in stripe or just canceled/came back so we can't call create users yet */
  checkForSavedFormsAfterUpdatingSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BillingStateActions.getBillingCountsAfterStripeRedirectSuccess),
      map((): AddTeammateFormData[] => JSON.parse(this.sessionStorage.get(ADD_TEAMMATES_FORM_DATA))),
      filter(userForms => !!userForms),
      concatLatestFrom(() => this.store.select(selectStripeQueryParams)),
      map(([userForms, stripeParams]) => {
        /** if there are stored user forms and updating license count was successful, try adding teammates.
         * otherwise reopen the dialog with the stored forms */
        return stripeParams?.reason == StripeReason.SubscriptionUpdateConfirmation
          ? AddTeammatesActions.addTeammatesToPaidCompany({ userForms })
          : AddTeammatesActions.openAddTeammatesDialog();
      })
    )
  );

  createUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.createUsers),
      concatLatestFrom(() => this.store.select(selectCompany)),
      map(([{ userForms }, company]) => userForms.map(f => NewUserDto.fromFormData(f, isGSGTargetCompany(company)))),
      exhaustMap((newUserDtos: NewUserDto[]) =>
        this.userService.createUsersV2(newUserDtos).pipe(
          map((users: User[]) => {
            this.sessionStorage.delete(ADD_TEAMMATES_FORM_DATA);
            return AddTeammatesActions.createUsersSuccess({ users });
          }),
          catchError((error: unknown) => of(AddTeammatesActions.createUsersFailed({ error })))
        )
      )
    )
  );

  handleCreateUsersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.createUsersSuccess),
      filter(({ users }) => !!users?.length),
      map(UsersStateActions.addMany)
    )
  );

  notifyCreateUsersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.createUsersSuccess),
      /** filter out v1 invite for now */
      filter(({ users }) => !!users?.length), // only notify if there are new users created
      map(({ users }) => {
        const newUser = users[0];

        let userDescription: string;
        if (users.length === 1) {
          userDescription = newUser.fullName;
          if (!userDescription) {
            userDescription = displayNameOrEmail(newUser);
          }
        } else {
          userDescription = `${users.length} users`;
        }
        const message = `${users.length === 1 ? 'has' : 'have'} been added to Ninety.`;
        return NotificationActions.notifyWithTemplate({
          templateType: SnackbarTemplateType.newUsersAdded,
          data: { title: userDescription, message },
        });
      })
    )
  );

  createUsersFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.createUsersFailed),
      map(({ error }) =>
        NotificationActions.notifyError({ error: error, message: 'Could not create users.  Please try again.' })
      )
    )
  );

  clearStripeQueryParamsAfterOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AddTeammatesActions.addTeammatesToPaidCompany, AddTeammatesActions.openAddTeammatesDialog),
      map(() => BillingStateActions.clearStripeQueryParams())
    )
  );
}
